import Style from "@/prototype/utils/Style.js";

import { DEGREES } from "@/prototype/projection/constants.js";
import { maxE, minE, maxN, minN, points_angle, points_distance, distance_to_string } from "@/prototype/projection/tools.js";
import Point from "@/prototype/projection/Point.js";

import ReferenceDebug from "./ReferenceDebug.js";

import global from "@/prototype/global.js";

const d3 = global.d3;
const log = global.log.getLogger("f_mtac.scene.projection.reference");
log.setLevel(log.levels.WARN);

const geonode_style = new Style();
const update_geonode_style = (enable) => {
    geonode_style.rule_set("geonode", `
    #geolocation .geonode:not(.geonode-trace) > g.color > circle {
        fill: ${enable ? "var(--mtac-blue)" : "var(--mtac-btn-gray)"};
    }
    circle.user_name {${enable ? "" : "fill: var(--mtac-btn-gray) !important"}}
    `);
};

/**
 * Reference is in charge of displaying the geolocation svg:
 * - a square between the geonodes,
 * - the diagonal and its distance
 * - an arrow to the north
 * @param {*} svg 
 * @param {*} objects 
 */
const Reference = function (svg, objects) {
    const NN = 0, EE = 1, SS = 2, WW = 3;
    const NE = 0, SE = 1, SW = 2, NW = 3;

    const group = objects.layers.geolocation.group;
    const axis = d3.create("svg:g").attr("class", "projection-axis");
    const line = [];

    for (let d = NN; d <= WW; d++) {
        line[d] = d3.create("svg:line")
            .attr("stroke", "var(--grid-color)")
            .attr("stroke-width", "3")
            .attr("opacity", "0.8")
            .attr("stroke-dasharray", "10,10");
        axis.node().appendChild(line[d].node());
    }

    const distance = d3.create("svg:line")
        .attr("stroke", "var(--grid-color)")
        .attr("stroke-width", "5")
        .attr("opacity", "0.8");
    axis.node().appendChild(distance.node());

    const distance_text_group = d3.create("svg:g").attr("class", "distance");
    const distance_text = d3.create("svg:text")
        .attr("text-anchor", "left")
        .attr("fill", "var(--grid-color-inverse)");
    const distance_rect = d3.create("svg:rect")
        .attr("rx", 11)
        .attr("fill", "var(--grid-color)");
    distance_text_group.node().appendChild(distance_rect.node());
    distance_text_group.node().appendChild(distance_text.node());
    axis.node().appendChild(distance_text_group.node());
    const distance_rect_render = () => {
        distance_text.attr("x", 0).attr("y", 0);
        var { width: w, height: h, x, y } = distance_text.node().getBBox();
        x = - w / 2; y = - h / 2 - y; // computing text position
        distance_text.attr("x", x).attr("y", y);
        w += 18; h += 6; // adding padding
        x = - w / 2; y = - h / 2; // computing box position
        distance_rect.attr("x", x).attr("y", y).attr("width", w).attr("height", h);
    };
    svg.layer_on_render("geolocation", distance_rect_render);

    // arrow indicating the north
    const alpha = Math.PI / 10; // arrow opening angle
    const D = 20; // arrow size
    const invSQRT2 = 1 / Math.sqrt(2);
    const V = invSQRT2 * D;
    const T = Math.tan(alpha) * V;

    const arrow_north_group = d3.create("svg:g");
    const arrow_north = d3.create("svg:polygon")
        .attr("fill", "var(--grid-color)")
        .attr("stroke", "var(--grid-color-inverse)")
        .attr("stroke-width", "0.5")
        .attr("class", "arrow-north");
    arrow_north.attr("points", [[0, 0], [T, 0], [0, -V], [-T, 0]]);
    arrow_north_group.node().appendChild(arrow_north.node());
    axis.node().appendChild(arrow_north_group.node());

    var reference_debugger = null;
    this.debug_enable = () => {
        if (reference_debugger !== null) {
            return;
        }
        reference_debugger = new ReferenceDebug();
    };

    this.debug_disable = () => {
        if (reference_debugger === null) {
            return;
        }
        reference_debugger.undraw();
        reference_debugger = null;
    };

     this.draw = (projection) => {
        var { A, B } = projection;

        var defined = (A !== null && B !== null);
        if (defined) {
            $("#command_area_bottom .georef-marker").css("display", null);
        } else {
            $("#command_area_bottom .georef-marker").hide();
        }

        update_geonode_style(defined);

        if (!defined) {
            this.undraw();
            return;
        }

        const P = [];

        NE, SE, SW, NW;
        for (let c = NE; c <= NW; c++) { // corners
            P[c] = new Point();
            P[c].EN = [(c <= SE) ? maxE(A, B) : minE(A, B), (c <= NE || c >= NW) ? maxN(A, B) : minN(A, B)];
            P[c].XY = projection.project(P[c].EN);
            let pcen = P[c].EN;
            let pcxy = P[c].XY;
            log.debug("Reference.draw corners", {c, pcen, pcxy});
        }

        NN, EE, SS, WW;
        for (let s = NN; s <= WW; s++) { // sides
            let c1 = s, c2 = (s + 1) % 4; // corners
            line[s].attr("x1", P[c1].X).attr("y1", P[c1].Y);
            line[s].attr("x2", P[c2].X).attr("y2", P[c2].Y);
        }

        distance.attr("x1", A.X).attr("y1", A.Y);
        distance.attr("x2", B.X).attr("y2", B.Y);

        distance_text_group.attr("transform", `translate(${(A.X + B.X) / 2}, ${(A.Y + B.Y) / 2})`);
        distance_text.text(distance_to_string(points_distance(A, B), 2)); // with precision = 2

        arrow_north.attr("x", (P[NE].X + P[NW].X) / 2).attr("y", (P[NE].Y + P[NW].Y) / 2);
        arrow_north_group.attr("transform", `translate(${(P[NE].X + P[NW].X) / 2},${(P[NE].Y + P[NW].Y) / 2}) rotate(${points_angle(P[NW], P[NE]) * DEGREES})`);

        if (reference_debugger !== null) {
            reference_debugger.draw(projection);
        }

        group.node().insertBefore(axis.node(), group.node().firstChild);

        if (svg.layer_rendered("geolocation")) {
            distance_rect_render();
        }
    };

    this.undraw = () => {
        axis.remove();
        this.debug_disable();
    };
};

export default Reference;
